import { useEffect, useState } from "react"
import type { AppShell } from "@/App"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Button } from "@/components/ui/button"
import {
  listBrands,
  upsertBrandMargin,
  type BrandRow,
} from "@/lib/api"

interface BrandMarginsTabProps {
  shell: AppShell | null
}

interface EditState {
  slug: string
  margin_with_battery: string
  margin_without_battery: string
  is_active: boolean
  sort_order: string
  dirty: boolean
  saving: boolean
}

function defaultSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

function toEditState(row: BrandRow): EditState {
  return {
    slug: row.solar_slug ?? defaultSlug(row.brand_name),
    margin_with_battery: row.margin_with_battery
      ? String(row.margin_with_battery)
      : "1.55",
    margin_without_battery: row.margin_without_battery
      ? String(row.margin_without_battery)
      : "1.65",
    is_active: !!row.is_active,
    sort_order: String(row.solar_sort_order ?? row.sort_order ?? 0),
    dirty: false,
    saving: false,
  }
}

export function BrandMarginsTab({ shell }: BrandMarginsTabProps) {
  const [brands, setBrands] = useState<BrandRow[]>([])
  const [edits, setEdits] = useState<Record<number, EditState>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function reload() {
    if (!shell) return
    setLoading(true)
    setError(null)
    try {
      const rows = await listBrands(shell)
      setBrands(rows)
      setEdits(
        Object.fromEntries(
          rows.map((row) => [row.brand_id, toEditState(row)]),
        ),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha a carregar marcas")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell])

  function patch(brandId: number, p: Partial<EditState>) {
    setEdits((prev) => ({
      ...prev,
      [brandId]: { ...prev[brandId], ...p, dirty: true },
    }))
  }

  async function save(brandId: number) {
    if (!shell) return
    const state = edits[brandId]
    if (!state) return
    setEdits((prev) => ({
      ...prev,
      [brandId]: { ...prev[brandId], saving: true },
    }))
    try {
      await upsertBrandMargin(shell, {
        brand_id: brandId,
        slug: state.slug.trim(),
        margin_with_battery: Number(state.margin_with_battery),
        margin_without_battery: Number(state.margin_without_battery),
        is_active: state.is_active,
        sort_order: Number.parseInt(state.sort_order, 10) || 0,
      })
      shell.bridge.toast({
        variant: "success",
        description: `Margens guardadas para ${
          brands.find((b) => b.brand_id === brandId)?.brand_name
        }`,
      })
      await reload()
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Falha a guardar"
      shell.bridge.toast({ variant: "error", description: msg })
      setEdits((prev) => ({
        ...prev,
        [brandId]: { ...prev[brandId], saving: false },
      }))
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">A carregar marcas…</p>
  if (error) return <p className="text-sm text-destructive">{error}</p>

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Define o slug solar e as margens c/ e s/ bateria por marca. Marcas
        inactivas não aparecem como opção no wizard.
      </p>
      <div className="overflow-hidden rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Marca</TableHead>
              <TableHead className="w-40">Slug solar</TableHead>
              <TableHead className="w-32">Margem c/ bateria</TableHead>
              <TableHead className="w-32">Margem s/ bateria</TableHead>
              <TableHead className="w-20">Activa</TableHead>
              <TableHead className="w-20">Ordem</TableHead>
              <TableHead className="w-24 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {brands.map((row) => {
              const state = edits[row.brand_id]
              if (!state) return null
              return (
                <TableRow key={row.brand_id}>
                  <TableCell className="font-medium">{row.brand_name}</TableCell>
                  <TableCell>
                    <Input
                      value={state.slug}
                      onChange={(e) =>
                        patch(row.brand_id, { slug: e.target.value })
                      }
                      placeholder="solis"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.0001"
                      value={state.margin_with_battery}
                      onChange={(e) =>
                        patch(row.brand_id, {
                          margin_with_battery: e.target.value,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.0001"
                      value={state.margin_without_battery}
                      onChange={(e) =>
                        patch(row.brand_id, {
                          margin_without_battery: e.target.value,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={state.is_active}
                      onCheckedChange={(v) =>
                        patch(row.brand_id, { is_active: v })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      value={state.sort_order}
                      onChange={(e) =>
                        patch(row.brand_id, { sort_order: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant={state.dirty ? "default" : "outline"}
                      disabled={!state.dirty || state.saving}
                      onClick={() => save(row.brand_id)}
                    >
                      {state.saving ? "A guardar…" : "Guardar"}
                    </Button>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
